import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiUrl } from '../utils/api';
import { formatDateOnly } from '../utils/dateOnly';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import CycleCasesModal from './modals/CycleCasesModal';
import MilestoneBurnupChart from './MilestoneBurnupChart';
import './TestPlans.css';

// "Plano de Teste" reaproveita a entidade Milestone que já existia no
// sistema (mesmos campos: nome, período, responsável, status; mesmo vínculo
// 1:N com execuções via milestone_id) — só adiciona "versão/release" e uma
// visão consolidada de progresso em cima do que já existe. Não é uma
// entidade nova no banco.

const STATUS_LABELS = { planned: 'Planejado', active: 'Em andamento', completed: 'Concluído' };
const STATUS_CLASS = { planned: 'status-pending', active: 'status-warning', completed: 'status-passed' };

// Mesmo vocabulário/cores usados em Execuções e no detalhe do Ciclo
// (CycleCasesModal) — não os rótulos do Zephyr, pra manter uma só forma de
// falar sobre status de caso no sistema todo.
const OUTCOME_SEGMENTS = [
  { key: 'completed', label: 'Aprovado', color: '#1C9A6C' },
  { key: 'bug', label: 'Bug', color: '#D6394A' },
  { key: 'improvement', label: 'Melhoria', color: '#B4740A' },
  { key: 'blocked', label: 'Bloqueado', color: '#5B6472' },
  { key: 'retest', label: 'Retestar', color: '#6D4FCC' }
  // "not_executed" fica de fora de propósito — não conta como progresso,
  // então não pinta a barra (mesma regra já aplicada em Execuções/Dashboard).
];

// A API de milestones não devolve o nome do responsável já resolvido — o
// mesmo padrão usado em Milestones.js: resolve pelo id na lista de usuários
// do projeto, caindo pra created_by quando o plano é antigo/sem responsável.
const getUserName = (users, userId) => {
  if (!userId) return '—';
  const u = (users || []).find(x => String(x.id) === String(userId));
  return u?.name || '—';
};

const formatDate = formatDateOnly;

const TestPlans = ({ projectId }) => {
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();

  const [plans, setPlans] = useState([]);
  const [statsByPlanId, setStatsByPlanId] = useState({});
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [archivedFilter, setArchivedFilter] = useState('active'); // 'active' | 'archived'

  const token = () => localStorage.getItem('token');
  const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` });

  const fetchPlans = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await fetch(getApiUrl(`milestones?project_id=${projectId}&archived=${archivedFilter}`), { headers: authHeaders() });
      const data = await res.json().catch(() => ([]));
      const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      setPlans(list);
      if (list.length > 0) {
        const ids = list.map(p => p.id).join(',');
        const statsRes = await fetch(getApiUrl(`milestones/stats/bulk?ids=${ids}`), { headers: authHeaders() });
        const statsData = await statsRes.json().catch(() => ({}));
        setStatsByPlanId(statsData?.stats || {});
      } else {
        setStatsByPlanId({});
      }
    } catch (err) {
      console.error('Erro ao carregar planos de teste:', err);
      showError('Erro ao carregar planos de teste');
    } finally {
      setLoading(false);
    }
  }, [projectId, archivedFilter, showError]);

  const fetchUsers = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await fetch(getApiUrl(`projects/${projectId}/access/users`), { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
      const normalized = list.map(u => ({
        id: u.user_id || u.id,
        name: u.name || u.full_name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email
      }));
      setUsers(normalized.length > 0 ? normalized : (user ? [{ id: user.id, name: user.name || user.email }] : []));
    } catch (_) {
      setUsers(user ? [{ id: user.id, name: user.name || user.email }] : []);
    }
  }, [projectId, user]);

  useEffect(() => {
    setSelectedPlanId(null);
    setShowCreateForm(false);
    fetchPlans();
    fetchUsers();
  }, [fetchPlans, fetchUsers]);

  const selectedPlan = useMemo(() => plans.find(p => String(p.id) === String(selectedPlanId)) || null, [plans, selectedPlanId]);

  // Atualiza o plano localmente em vez de recarregar a lista inteira: a
  // listagem por padrão só traz ativos (Fase 2), então um refetch aqui
  // faria o plano sumir do array e a tela de detalhe quebrar ("não
  // encontrado") logo depois de arquivar o próprio plano que você está
  // olhando. O filtro Ativos/Arquivados/Todos é a próxima fase — até lá,
  // arquivar/desarquivar só atualiza o registro em memória.
  const handleArchiveToggle = async (planId, isCurrentlyArchived) => {
    const action = isCurrentlyArchived ? 'unarchive' : 'archive';
    try {
      const res = await fetch(getApiUrl(`milestones/${planId}/${action}`), {
        method: 'PUT',
        headers: authHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(data?.error || `Erro ao ${isCurrentlyArchived ? 'desarquivar' : 'arquivar'} o plano`);
        return;
      }
      showSuccess(isCurrentlyArchived
        ? 'Plano desarquivado com sucesso'
        : `Plano arquivado com sucesso. Após ${data?.retention_days || 90} dias sem ser desarquivado, será excluído definitivamente.`);
      setPlans(prev => prev.map(p => (
        p.id === planId ? { ...p, archived_at: data.data.archived_at, archived_by: data.data.archived_by } : p
      )));
    } catch (err) {
      console.error('Erro ao arquivar/desarquivar plano:', err);
      showError(`Erro ao ${isCurrentlyArchived ? 'desarquivar' : 'arquivar'} o plano`);
    }
  };

  return (
    <div className="repository-content test-plans-content">
      <div className="main-content">
        <div className="content-body">
          {!selectedPlanId ? (
            <TestPlansList
              plans={plans}
              statsByPlanId={statsByPlanId}
              loading={loading}
              users={users}
              projectId={projectId}
              showCreateForm={showCreateForm}
              setShowCreateForm={setShowCreateForm}
              archivedFilter={archivedFilter}
              setArchivedFilter={setArchivedFilter}
              onSelect={(id) => setSelectedPlanId(id)}
              onCreated={fetchPlans}
              onArchiveToggle={handleArchiveToggle}
              authHeaders={authHeaders}
              showSuccess={showSuccess}
              showError={showError}
            />
          ) : (
            <TestPlanDetail
              plan={selectedPlan}
              projectId={projectId}
              users={users}
              onBack={() => setSelectedPlanId(null)}
              onUpdated={fetchPlans}
              onArchiveToggle={handleArchiveToggle}
              authHeaders={authHeaders}
              showSuccess={showSuccess}
              showError={showError}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------
// Lista de planos + criação
// ---------------------------------------------------------------------
const TestPlansList = ({ plans, statsByPlanId, loading, users, projectId, showCreateForm, setShowCreateForm, archivedFilter, setArchivedFilter, onSelect, onCreated, onArchiveToggle, authHeaders, showSuccess, showError }) => {
  const [form, setForm] = useState({ name: '', description: '', version: '', responsible_user_id: '', status: 'planned', start_date: '', due_date: '' });
  const [saving, setSaving] = useState(false);

  const resetForm = () => setForm({ name: '', description: '', version: '', responsible_user_id: '', status: 'planned', start_date: '', due_date: '' });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { showError('Nome é obrigatório'); return; }
    if (!form.responsible_user_id) { showError('Responsável é obrigatório'); return; }
    setSaving(true);
    try {
      const res = await fetch(getApiUrl('milestones'), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ ...form, project_id: projectId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(data?.error || 'Erro ao criar plano de teste');
        return;
      }
      showSuccess('Plano de teste criado com sucesso');
      resetForm();
      setShowCreateForm(false);
      onCreated();
    } catch (err) {
      console.error('Erro ao criar plano de teste:', err);
      showError('Erro ao criar plano de teste');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="test-plans-list">
      <div className="test-plans-header">
        <div>
          <h2>Planos de Teste</h2>
          <p className="test-plans-subtitle">Organize campanhas de teste agrupando ciclos e execuções, com uma visão consolidada do progresso.</p>
        </div>
        <div className="test-plans-header-actions">
          <select
            className="archived-filter-select"
            value={archivedFilter}
            onChange={(e) => setArchivedFilter(e.target.value)}
            title="Filtrar por arquivamento"
          >
            <option value="active">Ativos</option>
            <option value="archived">Arquivados</option>
          </select>
          <button className="btn btn-primary" onClick={() => setShowCreateForm(v => !v)}>
            {showCreateForm ? 'Cancelar' : '+ Novo Plano'}
          </button>
        </div>
      </div>

      {showCreateForm && (
        <form className="test-plan-form" onSubmit={handleCreate}>
          <div className="form-row">
            <div className="form-group">
              <label>Nome *</label>
              <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ex: Release 2.5.0" />
            </div>
            <div className="form-group">
              <label>Release/Versão</label>
              <input value={form.version} onChange={(e) => setForm(f => ({ ...f, version: e.target.value }))} placeholder="Ex: 2.5.0" />
            </div>
          </div>
          <div className="form-group">
            <label>Descrição</label>
            <textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} rows={2} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Responsável *</label>
              <select value={form.responsible_user_id} onChange={(e) => setForm(f => ({ ...f, responsible_user_id: e.target.value }))}>
                <option value="">Selecione...</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="planned">Planejado</option>
                <option value="active">Em andamento</option>
                <option value="completed">Concluído</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Início</label>
              <input type="date" value={form.start_date} onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Fim</label>
              <input type="date" value={form.due_date} onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))} />
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Criando...' : 'Criar Plano'}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="empty-state">Carregando planos...</div>
      ) : plans.length === 0 ? (
        <div className="empty-state">Nenhum plano de teste neste projeto ainda.</div>
      ) : (
        <table className="test-plans-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Versão</th>
              <th>Responsável</th>
              <th>Status</th>
              <th>Período</th>
              <th>Progresso</th>
              <th style={{ textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {plans.map(plan => {
              const stats = statsByPlanId[plan.id];
              const pct = stats?.progress_pct ?? 0;
              const isArchived = !!plan.archived_at;
              return (
                <tr key={plan.id} onClick={() => onSelect(plan.id)} className={`test-plan-row${isArchived ? ' is-archived' : ''}`}>
                  <td className="test-plan-name">
                    {plan.name}
                    {isArchived && <span className="archived-badge">🗄️ Arquivado</span>}
                  </td>
                  <td>{plan.version || '—'}</td>
                  <td>{getUserName(users, plan.responsible_user_id || plan.created_by)}</td>
                  <td><span className={`status-badge ${STATUS_CLASS[plan.status] || 'status-unknown'}`}>{STATUS_LABELS[plan.status] || plan.status}</span></td>
                  <td>{formatDate(plan.start_date)} → {formatDate(plan.due_date)}</td>
                  <td>
                    <div className="test-plan-mini-bar">
                      <div className="test-plan-mini-bar-fill" style={{ width: `${pct}%` }} />
                      <span>{pct}%</span>
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => onArchiveToggle(plan.id, isArchived)}
                    >
                      {isArchived ? '↩️ Desarquivar' : '🗄️ Arquivar'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------
// Detalhe consolidado do plano
// ---------------------------------------------------------------------
const TestPlanDetail = ({ plan, projectId, users, onBack, onUpdated, onArchiveToggle, authHeaders, showSuccess, showError }) => {
  const [stats, setStats] = useState(null);
  const [runs, setRuns] = useState([]);
  const [unlinkedRuns, setUnlinkedRuns] = useState([]);
  const [linking, setLinking] = useState(false);
  const [modalTarget, setModalTarget] = useState(null); // { cycleId, runId, executionId }
  const [showLinkPanel, setShowLinkPanel] = useState(false);
  const [linkSearch, setLinkSearch] = useState('');
  const [selectedToLink, setSelectedToLink] = useState(() => new Set());
  const [burnupData, setBurnupData] = useState(null);
  const [cycleArchivedMap, setCycleArchivedMap] = useState({}); // cycleId -> archived_at

  const fetchStats = useCallback(async (force = false) => {
    if (!plan?.id) return;
    try {
      // /:id/stats fica em cache por 60s no backend — força um recálculo
      // logo após uma ação que muda o resultado (vincular execução, editar
      // casos do ciclo), senão o progresso fica com número velho na tela.
      const url = `milestones/${plan.id}/stats${force ? '?force=1' : ''}`;
      const res = await fetch(getApiUrl(url), { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setStats(data?.stats || null);
    } catch (_) { /* silencioso — visão consolidada é auxiliar */ }
  }, [plan?.id, authHeaders]);

  const fetchRuns = useCallback(async () => {
    if (!plan?.id) return;
    try {
      // archived=all: esta é a visão de detalhe/rastreabilidade do plano —
      // diferente da listagem principal de Execuções, aqui uma execução ou
      // ciclo arquivado não deve sumir da tabela, só ganhar o badge (regra
      // 9/10: arquivamento não pode quebrar a rastreabilidade do plano).
      const res = await fetch(getApiUrl(`executions/runs?milestone_id=${plan.id}&archived=all`), { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      setRuns(Array.isArray(data?.data) ? data.data : []);
    } catch (_) {
      setRuns([]);
    }
  }, [plan?.id, authHeaders]);

  // Ciclo é uma entidade independente da execução — o archived_at que vem
  // junto de cada linha de `runs` é o da EXECUÇÃO, não o do ciclo. Busca à
  // parte pra saber se o ciclo em si está arquivado (usado pra decidir se
  // "Ver Casos" abre em modo leitura).
  const fetchCycleArchivedStates = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await fetch(getApiUrl(`test-repository/test-cycles?project_id=${projectId}&archived=all`), { headers: authHeaders() });
      const data = await res.json().catch(() => ([]));
      const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      const map = {};
      list.forEach(c => { map[c.id] = c.archived_at || null; });
      setCycleArchivedMap(map);
    } catch (_) {
      setCycleArchivedMap({});
    }
  }, [projectId, authHeaders]);

  const fetchUnlinkedRuns = useCallback(async () => {
    if (!projectId) return;
    try {
      // linking=1 já é o filtro usado pela tela de Milestones: só execuções
      // ainda sem milestone_id, elegíveis para vincular.
      const res = await fetch(getApiUrl(`executions/runs?project_id=${projectId}&linking=1`), { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      setUnlinkedRuns(Array.isArray(data?.data) ? data.data : []);
    } catch (_) {
      setUnlinkedRuns([]);
    }
  }, [projectId, authHeaders]);

  const fetchBurnup = useCallback(async () => {
    if (!plan?.id) return;
    try {
      const res = await fetch(getApiUrl(`milestones/${plan.id}/burnup`), { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setBurnupData(data);
    } catch (_) {
      setBurnupData(null);
    }
  }, [plan?.id, authHeaders]);

  useEffect(() => {
    fetchStats();
    fetchRuns();
    fetchBurnup();
    fetchUnlinkedRuns();
    fetchCycleArchivedStates();
  }, [fetchStats, fetchRuns, fetchBurnup, fetchUnlinkedRuns, fetchCycleArchivedStates]);

  const cycles = useMemo(() => {
    const seen = new Map();
    runs.forEach(r => {
      const cid = r.linked_cycle_id || r.cycle_id;
      if (!cid || seen.has(cid)) return;
      seen.set(cid, {
        id: cid,
        name: r.linked_cycle_name || r.cycle_name || 'Ciclo',
        sampleRunId: r.id,
        sampleExecutionId: r.id,
        archived_at: cycleArchivedMap[cid] || null
      });
    });
    return Array.from(seen.values());
  }, [runs, cycleArchivedMap]);

  const filteredUnlinkedRuns = useMemo(() => {
    const term = linkSearch.trim().toLowerCase();
    if (!term) return unlinkedRuns;
    return unlinkedRuns.filter(r => String(r.name || '').toLowerCase().includes(term));
  }, [unlinkedRuns, linkSearch]);

  const toggleRunToLink = (id) => {
    setSelectedToLink(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedToLink(prev => {
      const allVisible = filteredUnlinkedRuns.map(r => r.id);
      const allSelected = allVisible.length > 0 && allVisible.every(id => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        allVisible.forEach(id => next.delete(id));
        return next;
      }
      return new Set([...prev, ...allVisible]);
    });
  };

  const handleLink = async () => {
    const runIds = Array.from(selectedToLink);
    if (runIds.length === 0) return;
    setLinking(true);
    try {
      const res = await fetch(getApiUrl('executions/runs/batch-link-milestone'), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ milestone_id: plan.id, run_ids: runIds })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showError(data?.error || 'Erro ao vincular execuções');
        return;
      }
      showSuccess(runIds.length === 1 ? 'Execução vinculada ao plano' : `${runIds.length} execuções vinculadas ao plano`);
      setSelectedToLink(new Set());
      setLinkSearch('');
      setShowLinkPanel(false);
      fetchRuns();
      fetchUnlinkedRuns();
      fetchStats(true);
      fetchBurnup();
    } catch (err) {
      console.error('Erro ao vincular execuções:', err);
      showError('Erro ao vincular execuções');
    } finally {
      setLinking(false);
    }
  };

  if (!plan) {
    return (
      <div className="empty-state">
        Plano não encontrado.
        <div style={{ marginTop: 12 }}>
          <button className="btn btn-secondary" onClick={onBack}>← Voltar</button>
        </div>
      </div>
    );
  }

  const total = stats?.case_total || 0;
  const byOutcome = stats?.by_outcome || { completed: 0, bug: 0, improvement: 0, blocked: 0, retest: 0, not_executed: 0 };
  const segments = OUTCOME_SEGMENTS
    .map(seg => ({ ...seg, count: byOutcome[seg.key] || 0, pct: total ? ((byOutcome[seg.key] || 0) / total) * 100 : 0 }))
    .filter(seg => seg.count > 0);
  const executedPct = stats?.executed_pct ?? 0;
  const isArchived = !!plan.archived_at;

  // "Ver Casos" troca a tela inteira pelo CycleCasesModal (mesmo padrão do
  // Repositório de Testes/Execuções) em vez de anexar embaixo de tudo —
  // antes disso o clique parecia não fazer nada, porque o conteúdo só
  // aparecia depois de rolar a página inteira (Progresso, burnup, tabelas).
  if (modalTarget) {
    return (
      <CycleCasesModal
        isOpen={true}
        onClose={() => { setModalTarget(null); fetchStats(true); fetchBurnup(); onUpdated(); }}
        cycleId={modalTarget.cycleId}
        runId={modalTarget.runId}
        executionId={modalTarget.executionId}
        isReadOnly={!!modalTarget.archivedAt}
      />
    );
  }

  return (
    <div className="test-plan-detail">
      <div className="test-plan-detail-header">
        <button className="btn btn-secondary" onClick={onBack}>← Voltar</button>
        <div className="test-plan-detail-title">
          <h2>{plan.name}</h2>
          {plan.version && <span className="test-plan-version-chip">{plan.version}</span>}
          <span className={`status-badge ${STATUS_CLASS[plan.status] || 'status-unknown'}`}>{STATUS_LABELS[plan.status] || plan.status}</span>
          {isArchived && <span className="archived-badge">🗄️ Arquivado</span>}
        </div>
        <button
          className="btn btn-secondary"
          style={{ marginLeft: 'auto' }}
          onClick={() => onArchiveToggle(plan.id, isArchived)}
        >
          {isArchived ? '↩️ Desarquivar' : '🗄️ Arquivar'}
        </button>
      </div>
      {isArchived && (
        <div className="archived-notice">
          Este plano está arquivado. Ele continua disponível para consulta, mas ações como vincular novas execuções ficam bloqueadas. Desarquive para retomar o uso normal.
        </div>
      )}

      <div className="test-plan-detail-grid">
        <div className="test-plan-card">
          <div className="test-plan-card-title">Informações</div>
          <div className="test-plan-info-row"><span>Descrição</span><strong>{plan.description || '—'}</strong></div>
          <div className="test-plan-info-row"><span>Responsável</span><strong>{getUserName(users, plan.responsible_user_id || plan.created_by)}</strong></div>
          <div className="test-plan-info-row"><span>Período</span><strong>{formatDate(plan.start_date)} → {formatDate(plan.due_date)}</strong></div>
        </div>

        <div className="test-plan-card">
          <div className="test-plan-card-title">Progresso</div>
          <div className="test-plan-progress-rail">
            {segments.map(seg => (
              <div key={seg.key} title={`${seg.label}: ${seg.count}`} style={{ width: `${seg.pct}%`, background: seg.color }} />
            ))}
            <span className={`test-plan-progress-pct${segments.reduce((s, x) => s + x.pct, 0) < 50 ? ' on-empty' : ''}`}>{executedPct}%</span>
          </div>
          <div className="test-plan-outcome-legend">
            <span>Total <b>{total}</b></span>
            <span style={{ color: '#1C9A6C' }}>Aprovado <b>{byOutcome.completed || 0}</b></span>
            <span style={{ color: '#D6394A' }}>Bug <b>{byOutcome.bug || 0}</b></span>
            <span style={{ color: '#B4740A' }}>Melhoria <b>{byOutcome.improvement || 0}</b></span>
            <span style={{ color: '#5B6472' }}>Bloqueado <b>{byOutcome.blocked || 0}</b></span>
            <span style={{ color: '#6D4FCC' }}>Retestar <b>{byOutcome.retest || 0}</b></span>
            <span style={{ color: '#8D96A5' }}>Não Executado <b>{byOutcome.not_executed || 0}</b></span>
          </div>
        </div>
      </div>

      <div className="test-plan-card">
        <div className="test-plan-card-title">Progresso ao longo do tempo</div>
        {plan.start_date && plan.due_date ? (
          <MilestoneBurnupChart milestone={plan} executions={runs} burnupData={burnupData} />
        ) : (
          <div className="empty-metric">Defina um período (início e fim) neste plano para acompanhar o burnup.</div>
        )}
      </div>

      <div className="test-plan-card">
        <div className="test-plan-card-title">Ciclos de Teste vinculados</div>
        {cycles.length === 0 ? (
          <div className="empty-metric">Nenhum ciclo vinculado a este plano ainda.</div>
        ) : (
          <table className="test-plans-table">
            <thead><tr><th>Ciclo</th><th style={{ textAlign: 'right' }}>Ações</th></tr></thead>
            <tbody>
              {cycles.map(c => (
                <tr key={c.id}>
                  <td>
                    {c.name}
                    {c.archived_at && <span className="archived-badge">🗄️ Arquivado</span>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setModalTarget({ cycleId: c.id, runId: c.id, executionId: c.sampleExecutionId, archivedAt: c.archived_at })}
                    >
                      {c.archived_at ? '👁️ Ver Casos (somente leitura)' : 'Ver Casos'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="test-plan-card">
        <div className="test-plan-card-title-row">
          <div className="test-plan-card-title">Execuções</div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setShowLinkPanel(v => !v)}
            disabled={isArchived || unlinkedRuns.length === 0}
            title={isArchived ? 'Plano arquivado: desarquive para vincular execuções' : (unlinkedRuns.length === 0 ? 'Não há execuções sem plano neste projeto' : undefined)}
          >
            {showLinkPanel ? 'Cancelar' : '🔗 Vincular Execuções'}
          </button>
        </div>

        {showLinkPanel && (
          <div className="test-plan-link-panel">
            <div className="test-plan-link-panel-header">
              <input
                type="text"
                placeholder="Buscar execução pelo nome..."
                value={linkSearch}
                onChange={(e) => setLinkSearch(e.target.value)}
              />
              <label className="test-plan-select-all">
                <input
                  type="checkbox"
                  checked={filteredUnlinkedRuns.length > 0 && filteredUnlinkedRuns.every(r => selectedToLink.has(r.id))}
                  onChange={toggleSelectAllVisible}
                />
                Selecionar todas ({filteredUnlinkedRuns.length})
              </label>
            </div>
            <div className="test-plan-link-list">
              {filteredUnlinkedRuns.length === 0 ? (
                <div className="empty-metric">Nenhuma execução encontrada.</div>
              ) : (
                filteredUnlinkedRuns.map(r => (
                  <label key={r.id} className="test-plan-link-item">
                    <input
                      type="checkbox"
                      checked={selectedToLink.has(r.id)}
                      onChange={() => toggleRunToLink(r.id)}
                    />
                    <span className="test-plan-link-item-name">{r.name}</span>
                    <span className={`status-badge status-${String(r.status || '').toLowerCase()}`}>{r.status}</span>
                    {(r.linked_cycle_name || r.cycle_name) && (
                      <span className="test-plan-link-item-cycle">{r.linked_cycle_name || r.cycle_name}</span>
                    )}
                  </label>
                ))
              )}
            </div>
            <div className="test-plan-link-panel-footer">
              <button
                className="btn btn-primary"
                onClick={handleLink}
                disabled={selectedToLink.size === 0 || linking}
              >
                {linking
                  ? 'Vinculando...'
                  : selectedToLink.size === 0
                    ? 'Vincular execuções'
                    : `Vincular ${selectedToLink.size} execução${selectedToLink.size === 1 ? '' : 'ões'}`}
              </button>
            </div>
          </div>
        )}

        {runs.length === 0 ? (
          <div className="empty-metric">Nenhuma execução vinculada a este plano ainda.</div>
        ) : (
          <table className="test-plans-table">
            <thead><tr><th>Execução</th><th>Status</th><th>Ciclo</th></tr></thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.id}>
                  <td>
                    {r.name}
                    {r.archived_at && <span className="archived-badge">🗄️ Arquivado</span>}
                  </td>
                  <td><span className={`status-badge status-${String(r.status || '').toLowerCase()}`}>{r.status}</span></td>
                  <td>{r.linked_cycle_name || r.cycle_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TestPlans;
